import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { QUERY_ORDER_TABLE_PROFILES, QUERY_ORDER_TABLE_PROFILE_BY_PK } from '@/graphql/queries/order_table_profiles';
import {
    DELETE_ORDER_TABLE_PROFILE_BY_PK,
    INSERT_ORDER_TABLE_PROFILE,
    UPDATE_ORDER_TABLE_PROFILE_BY_PK,
} from '@/graphql/mutations/order_table_profiles';

const getActiveProfileStorageKey = ({ tableType, userId }) => {
    const resolvedUserId = userId || 'unknown';
    return `order_table_active_profile:${tableType}:${resolvedUserId}`;
};

/**
 * Shared order-table profiles abstraction.
 *
 * This hook centralizes:
 * - profile list query + CRUD mutations
 * - active profile selection (incl localStorage persistence)
 * - import-by-id
 *
 * Page-specific logic stays in the caller via:
 * - buildProfileSettings()
 * - applyProfileSettings(settings)
 * - onResetToDefault()
 */
export const useOrderTableProfiles = ({
    tableType,
    userId,
    buildProfileSettings,
    applyProfileSettings,
    onResetToDefault,
    notifyError,
}) => {
    const [selectedProfileId, setSelectedProfileId] = useState('');

    const activeProfileStorageKey = useMemo(
        () => getActiveProfileStorageKey({ tableType, userId }),
        [tableType, userId]
    );

    const {
        data: profilesData,
        loading: profilesLoading,
        refetch: refetchProfiles,
    } = useQuery(QUERY_ORDER_TABLE_PROFILES, {
        variables: {
            where: { user_id: { _eq: userId }, table_type: { _eq: tableType } },
            order_by: [{ name: 'asc' }],
        },
        skip: !userId || !tableType,
    });

    const profiles = useMemo(() => profilesData?.order_table_profiles || [], [profilesData]);
    const selectedProfile = useMemo(
        () => profiles.find((p) => p.order_table_profile_id === selectedProfileId),
        [profiles, selectedProfileId]
    );

    const currentProfileName = selectedProfile?.name || 'Default';
    const currentProfileId = selectedProfile?.order_table_profile_id || '-';

    const [loadProfileByPk] = useLazyQuery(QUERY_ORDER_TABLE_PROFILE_BY_PK, {
        fetchPolicy: 'network-only',
    });

    const handleImportProfileById = useCallback(
        async (order_table_profile_id) => {
            const { data } = await loadProfileByPk({
                variables: { order_table_profile_id },
            });
            return data?.order_table_profiles_by_pk || null;
        },
        [loadProfileByPk]
    );

    const handleError = useCallback(
        (error, fallbackMessage) => {
            captureException(error);
            notifyError?.(error?.message || fallbackMessage);
        },
        [notifyError]
    );

    const resetToDefaultProfile = useCallback(() => {
        setSelectedProfileId('');
        localStorage.removeItem(activeProfileStorageKey);
        onResetToDefault?.();
    }, [activeProfileStorageKey, onResetToDefault]);

    const [insertProfile, { loading: insertProfileLoading }] = useMutation(INSERT_ORDER_TABLE_PROFILE, {
        onError: (error) => handleError(error, 'Failed to save profile.'),
        onCompleted: async (res) => {
            const created = res?.insert_order_table_profiles_one;
            if (created?.order_table_profile_id) {
                setSelectedProfileId(created.order_table_profile_id);
                localStorage.setItem(activeProfileStorageKey, created.order_table_profile_id);
            }
            await refetchProfiles?.();
        },
    });

    const [updateProfile, { loading: updateProfileLoading }] = useMutation(UPDATE_ORDER_TABLE_PROFILE_BY_PK, {
        onError: (error) => handleError(error, 'Failed to overwrite profile.'),
        onCompleted: async () => {
            await refetchProfiles?.();
        },
    });

    const [deleteProfile, { loading: deleteProfileLoading }] = useMutation(DELETE_ORDER_TABLE_PROFILE_BY_PK, {
        onError: (error) => handleError(error, 'Failed to delete profile.'),
        onCompleted: async () => {
            resetToDefaultProfile();
            await refetchProfiles?.();
        },
    });

    const handleSaveNewProfile = useCallback(
        (name, settingsOverride) => {
            const trimmed = (name || '').trim();
            if (!trimmed) return;

            const settingsToSave = settingsOverride || buildProfileSettings?.();

            if (settingsOverride) {
                // imported profile
                applyProfileSettings?.(settingsOverride);
            }

            insertProfile({
                variables: {
                    object: {
                        user_id: userId,
                        table_type: tableType,
                        name: trimmed,
                        settings: settingsToSave,
                    },
                },
            });
        },
        [applyProfileSettings, buildProfileSettings, insertProfile, tableType, userId]
    );

    const handleOverwriteProfile = useCallback(
        (name) => {
            if (!selectedProfileId) return;
            const updateData = {
                settings: buildProfileSettings?.(),
                last_modified_at: new Date().toISOString(),
            };
            if (name !== undefined && name !== null) {
                const trimmed = (name || '').trim();
                if (trimmed) {
                    updateData.name = trimmed;
                }
            }
            updateProfile({
                variables: {
                    order_table_profile_id: selectedProfileId,
                    set: updateData,
                },
            });
        },
        [buildProfileSettings, selectedProfileId, updateProfile]
    );

    const handleDeleteCurrentProfile = useCallback(() => {
        if (!selectedProfileId) return;
        deleteProfile({ variables: { order_table_profile_id: selectedProfileId } });
    }, [deleteProfile, selectedProfileId]);

    const selectProfileById = useCallback(
        (id) => {
            if (!id) {
                resetToDefaultProfile();
                return;
            }

            setSelectedProfileId(id);
            localStorage.setItem(activeProfileStorageKey, id);

            const match = profiles.find((p) => p.order_table_profile_id === id);
            if (match) {
                applyProfileSettings?.(match.settings || {});
            }
        },
        [activeProfileStorageKey, applyProfileSettings, profiles, resetToDefaultProfile]
    );

    // Auto-load active profile from localStorage once profiles are loaded
    useEffect(() => {
        if (!profiles?.length) return;
        if (selectedProfileId) return;

        const stored = localStorage.getItem(activeProfileStorageKey);
        if (!stored) return;

        const match = profiles.find((p) => p.order_table_profile_id === stored);
        if (!match) {
            localStorage.removeItem(activeProfileStorageKey);
            return;
        }

        setSelectedProfileId(stored);
        applyProfileSettings?.(match.settings || {});
    }, [profiles, selectedProfileId, activeProfileStorageKey, applyProfileSettings]);

    const profileSelectProps = useMemo(
        () => ({
            value: selectedProfileId || '',
            disabled: profilesLoading,
            SelectProps: {
                displayEmpty: true,
                renderValue: (val) => {
                    if (!val) return 'Default';
                    return profiles.find((p) => p.order_table_profile_id === val)?.name;
                },
            },
            onChange: (e) => selectProfileById(e.target.value),
        }),
        [profiles, profilesLoading, selectProfileById, selectedProfileId]
    );

    return {
        // data
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,

        // localStorage
        activeProfileStorageKey,

        // actions
        selectProfileById,
        resetToDefaultProfile,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,

        // mutation load states
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,

        // UI helpers
        profileSelectProps,
    };
};
